import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BubbleButton } from '@go-client/components/BubbleUI';
import { RESERVABLE_EXPERIENCE_TYPE } from '@go-shared/types/reservable';
import { Alert } from '@spark-labs/avocado-ui';
import React from 'react';

interface SelectExperienceCardOptions {
  key: RESERVABLE_EXPERIENCE_TYPE;
  title: string;
  description: string;
  icon: IconProp;
  items: string[];
}

interface ReservableExperienceTypeSelectorProps {
  experienceType: RESERVABLE_EXPERIENCE_TYPE;
  modeType: 'create' | 'edit' | 'view' | 'filter';
  disabled?: boolean;
  onValue: (nextType: RESERVABLE_EXPERIENCE_TYPE) => void;
}

const options: SelectExperienceCardOptions[] = [
  {
    key: 'ticket',
    title: 'Tickets',
    description: 'Sell spots for a scheduled experience',
    icon: 'ticket',
    items: [
      'Guests purchase quantities (adults, children, etc.)',
      'One-time or recurring sessions',
      'Capacity-based, not tied to a physical item',
      'Best for classes, tours, shows & group activities',
    ],
  },
  {
    key: 'resource',
    title: 'Rentals',
    description: 'Reserve specific physical items',
    icon: 'diamonds-4',
    items: [
      'Guests book a specific unit (cabana, court, room)',
      'Availability managed per item',
      'Time-based reservations',
      'Best for cabanas, rentals, equipment & spaces',
    ],
  },
];

export default function ReservableExperienceTypeSelector(
  props: ReservableExperienceTypeSelectorProps
) {
  const { experienceType, modeType, disabled } = props;

  return (
    <fieldset disabled={disabled} className="grid grid-cols-2 gap-4 items-stretch">
      {options.map(o => (
        <BubbleButton
          key={o.key}
          variant={o.key === experienceType ? 'navy' : 'white'}
          className="border border-navy"
          onClick={async () => {
            if (o.key === experienceType) return;
            if (modeType === 'edit') {
              const confirmed = await Alert.question({
                title: 'Change experience type?',
                message:
                  'Switching types may have unintended consequences, including double booking risks! Ensure reservations are properly managed after the change.',
                yesButton: 'Change',
                noButton: 'Cancel',
              });
              if (!confirmed) return;
            }
            props.onValue(o.key);
          }}
        >
          <div className="text-left flex items-start p-3">
            <FontAwesomeIcon icon={o.icon} className="mr-4 mt-1 text-xl" />
            <div>
              <h1
                className={`font-semibold text-lg mb-2 ${o.key === experienceType ? 'text-white' : 'text-navy'}`}
              >
                {o.title}
              </h1>
              <div className={`mb-2 ${o.key === experienceType ? 'text-eggshell' : 'text-slate'}`}>
                {o.description}
              </div>
              <div
                className={`font-normal grid grid-cols-1 gap-2 ${o.key === experienceType ? 'text-eggshell' : 'text-slate'}`}
              >
                {o.items.map((itm, idx) => (
                  <div key={idx} className="flex items-start whitespace-normal">
                    <FontAwesomeIcon icon="check-circle" className="mr-2 mt-1" />
                    <span>{itm}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </BubbleButton>
      ))}
    </fieldset>
  );
}